import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Info } from 'lucide-react';

import { Button } from 'components/ui/button';
import { PageHeader } from 'components/page/page-header';
import { LogoutIcon, UserIcon } from 'components/ob-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';

import ChangeEmailComponent from './email';
import { SettingsPanel, SettingsRow } from './settings-panel';
import PasswordChangeForm from './PasswordChangeForm';
import { API_BASE_URL } from '../../../config/constants';
import { makeApiRequest } from '../../../lib/http/makeApiRequest';
import { toast } from 'sonner';
import { Modal } from 'antd';
import { useAtom } from 'jotai';
import { user } from '../../../state/atoms/user.atom';
import { useNavigate } from 'react-router-dom';

const SUBSCRIPTION_TOOLTIP_TEXT = 'The subscription management dashboard will be available after your purchase of a pricing plan or proxy. Here you can check your service status, change your card, and cancel your subscription.';
const SUBSCRIPTION_MODAL_TEXT = 'The subscription management dashboard will be available after your purchase of a pricing plan or proxy.';

export const OverviewPage: FC = () => {
  const { t: translation, i18n } = useTranslation();
  const navigate = useNavigate();

  const [language, setLanguage] = useState(i18n.language);
  const [isEmailChangeModalOpened, setIsEmailChangeModalOpened] = useState<boolean>(false);
  const [isPasswordChangeModalOpened, setIsPasswordChangeModalOpened] = useState<boolean>(false);
  const [isSubscriptionModalOpened, setIsSubscriptionModalOpened] = useState<boolean>(false);
  const [confirmEmailSent, setConfirmEmailSent] = useState<boolean>(false);
  const [userData, setUserData] = useAtom(user);
  const isFreePlan = userData?.planName === 'Free';
  const canManageSubscription = !isFreePlan || Boolean(userData?.hasStripePayment);

  useEffect(() => {
    const locale = localStorage.getItem('@gologin:selected_language');
    setLanguage(locale || i18n.language);
  }, []);

  const confirmEmail = async (): Promise<void> => {
    try {
      await makeApiRequest(`/v1/auth/email/verify`, {
        method: 'POST',
      });
      setConfirmEmailSent(true);
      toast.success(translation('personalArea.overview.AlertEmailConfirm'));
    } catch (error) {
      console.error('Error confirming email:', error);
    }
  };

  const changeLanguage = (code: string): void => {
    i18n.changeLanguage(code).then(() => {
      setLanguage(i18n.language);
      moment.locale(i18n.language);
      localStorage.setItem('@gologin:selected_language', i18n.language);
    });
  };

  const openPricing = () => navigate('/pricing');
  const onEmailChanged = async () => {
    setIsEmailChangeModalOpened(false);
    try {
      const userResponse = await makeApiRequest('/v1/user', {
        method: 'GET',
      });
      setUserData(userResponse);
    } catch (error) {
      console.error('Error loading user after email change:', error);
    }
  };

  const createBillingPortalSession = async () => {
    try {
      const session = await makeApiRequest('/v1/payment/stripe/customer-portal');
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (error) {
      const apiError = error as Error & { status?: number };
      if (apiError.status === 404) {
        navigate('/pricing');
      } else {
        console.error('Error creating billing portal session:', error);
      }
    }
  };

  const handleManageSubscription = () => {
    if (!canManageSubscription) {
      setIsSubscriptionModalOpened(true);

      return;
    }

    createBillingPortalSession();
  };

  const handleUpdatePlan = () => {
    setIsSubscriptionModalOpened(false);
    openPricing();
  };

  const settingsActionClassName = 'min-w-[126px] shrink-0';

  return (
    <div className='mx-auto w-[calc(100%-32px)] py-8 md:w-[min(880px,calc(100%-96px))] md:pb-16 md:pt-12'>
      <PageHeader
        icon={UserIcon}
        title={translation('Account settings')}
        description={translation('Manage your profile, sign-in details, and subscription.')}
      />

      <SettingsPanel title={translation('Profile')} description={translation('Your account email.')}>
        <SettingsRow
          label={translation('personalArea.overview.subTitleEmail')}
          value={userData?.email || '—'}
          title={userData?.email}
          action={!userData?.emailVerified ? (
            <Button
              className={settingsActionClassName}
              variant='ob-outline'
              size='pill-sm'
              onClick={confirmEmail}
              disabled={confirmEmailSent}
            >
              {confirmEmailSent
                ? translation('Confirmation email sent')
                : translation('personalArea.overview.confirmSubmitBtnText')}
            </Button>
          ) : (
            <Button
              className={settingsActionClassName}
              variant='ob-outline'
              size='pill-sm'
              onClick={() => setIsEmailChangeModalOpened(true)}
            >
              {translation('Change email')}
            </Button>
          )}
        />
      </SettingsPanel>

      <SettingsPanel title={translation('Security')} description={translation('Keep access to your account protected.')}>
        <SettingsRow
          label={translation('base.password')}
          value={<span className='tracking-[.16em]'>••••••••••••</span>}
          action={(
            <Button
              className={settingsActionClassName}
              variant='ob-outline'
              size='pill-sm'
              onClick={() => setIsPasswordChangeModalOpened(true)}
            >
              {translation('changePassInSettings.changePassword')}
            </Button>
          )}
        />
      </SettingsPanel>

      <SettingsPanel title={translation('Subscription')} description={translation('Your current plan and profile allowance.')}>
        <div className='flex flex-col items-start justify-between gap-4 px-6 py-5 sm:flex-row sm:items-center'>
          <div className='flex flex-wrap items-center gap-3'>
            <span className='rounded-[13px] bg-ob-canvas px-2.5 py-[5px] text-[13px] text-ob-text-muted'>
              {userData?.planName ? `${userData.planName} plan` : '—'}
            </span>
            {userData?.limits?.activeProfiles != null && (
              <strong className='font-medium text-ob-text-secondary'>
                {userData.limits.activeProfiles} profiles
              </strong>
            )}
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            {!canManageSubscription && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className='inline-flex text-ob-text-subtle hover:text-ob-text'
                      aria-label={translation(SUBSCRIPTION_TOOLTIP_TEXT)}
                    >
                      <Info className='h-4 w-4' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs whitespace-normal'>
                    {translation(SUBSCRIPTION_TOOLTIP_TEXT)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button
              className={settingsActionClassName}
              variant='ob-outline'
              size='pill-sm'
              onClick={handleManageSubscription}
            >
              {translation('Manage Subscription')}
            </Button>
            <Button
              className={settingsActionClassName}
              variant='ob-accent'
              size='pill-sm'
              onClick={openPricing}
            >
              {translation('View plans')}
            </Button>
          </div>
        </div>
      </SettingsPanel>

      <section className='mt-7 flex min-h-[84px] flex-col items-start justify-between gap-4 rounded-2xl border border-[#754e52] px-6 py-[18px] sm:flex-row sm:items-center sm:gap-6'>
        <div>
          <h2 className='text-lg font-medium text-ob-text'>{translation('Session')}</h2>
          <p className='mt-1.5 text-sm text-ob-text-subtle'>{translation('Sign out of 1browser on this device.')}</p>
        </div>
        <form action={`${API_BASE_URL}/v1/auth/logout`} method='POST'>
          <Button
            type='submit'
            variant='ob-danger'
            className='h-10 px-[18px] text-base'
            onClick={() => {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
            }}
          >
            <LogoutIcon className='h-4 w-4' />
            Log out
          </Button>
        </form>
      </section>

      {/* Modals */}
      <Modal
        visible={isEmailChangeModalOpened}
        onCancel={(): void => setIsEmailChangeModalOpened(false)}
        title={translation('personalArea.changeEmailModal.title')}
        footer={null}
      >
        <ChangeEmailComponent
          initialEmail={userData?.email}
          onConfirm={onEmailChanged}
        />
      </Modal>
      {/* <Modal
        visible={isPaymentModalOpened}
        onCancel={(): void => setIsPaymentModalOpened(false)}
        title='Unlimited access'
        footer={null}
      >
        <div>
          <Button onClick={() => createPaymentSession({period: 'month', plan: '6724d0a7fdcb2bc43d1a4214'})} style={{marginRight: 20}}>$19/month</Button>
          <Button onClick={() => createPaymentSession({period: 'year', plan: '6724d0a7fdcb2bc43d1a4214'})}>$160/year (30% discount) </Button>
        </div>
      </Modal> */}
      <Modal
        visible={isPasswordChangeModalOpened}
        onCancel={(): void => setIsPasswordChangeModalOpened(false)}
        title={translation('changePassInSettings.changePassword')}
        footer={null}
      >
        <PasswordChangeForm
          onSubmit={(): void => setIsPasswordChangeModalOpened(false)}
        />
      </Modal>
      <Dialog open={isSubscriptionModalOpened} onOpenChange={setIsSubscriptionModalOpened}>
        <DialogContent className='max-w-md'>
          <DialogTitle className='sr-only'>
            {translation('Manage Subscription')}
          </DialogTitle>
          <DialogDescription className='text-sm text-ob-text-secondary'>
            {translation(SUBSCRIPTION_MODAL_TEXT)}{' '}
            <button
              type='button'
              className='text-ob-primary underline hover:opacity-80'
              onClick={handleUpdatePlan}
            >
              {translation('Update plan')}
            </button>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </div>
  );
};
